import * as React from "react"
import CircularProgress from "@mui/material/CircularProgress"
import { Icon, Typography, Box } from "@mui/material"
import Button, { ButtonProps } from "@mui/material/Button"
import { styled } from "@mui/material/styles"
import { TypographyVariant } from "types/material/typography"

interface OreveBlockButtonProps extends ButtonProps {
    loading?: boolean
    value?: string
    icon?: React.ElementType | null
    typography?: TypographyVariant
    removeMargin?: boolean
    selected?: boolean
}

const StyledButton = styled(Button)<ButtonProps>({
    position: "relative",
    borderRadius: "10px",
    minWidth: "0",
    padding: "16px 24px",
    textTransform: "none",
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    width: "391px",
    height: "56px",
})

const baseDisabledSx = {
    backgroundColor: "grey.100",
    color: "grey.400",
    borderColor: "transparent",
}

const variantsSx = {
    contained: (selected: boolean) => ({
        backgroundColor: selected ? "grey.300" : "grey.100",
        color: "info.main",
        border: selected ? "2px solid black" : "none",
        "&:hover, &:focus": {
            backgroundColor: selected ? "grey.400" : "grey.200",
        },
        "&:disabled": baseDisabledSx,
    }),
    outlined: (selected: boolean) => ({
        border: selected ? "2px solid black" : "1px solid grey",
        backgroundColor: "transparent",
        "&:hover, &:focus": {
            backgroundColor: "grey.200",
        },
        "&:disabled": baseDisabledSx,
    }),
    text: (selected: boolean) => ({
        backgroundColor: "transparent",
        color: selected ? "black" : "grey",
        textDecoration: "underline",
        textUnderlineOffset: "0.3em",
        "&:hover, &:focus": {
            textDecoration: "underline",
        },
        "&:disabled": {
            color: "grey.400",
        },
    }),
}

const computeMargin = (
    value: string,
    removeMargin: boolean
): string | undefined => {
    if (removeMargin) return undefined
    if (value === "") return "1em"
    return "0.7em 1.5em"
}

export const OreveBlockButton = ({
    loading = false,
    variant = "contained",
    color = "primary",
    icon = null,
    value = "",
    removeMargin = false,
    selected = false,
    typography = "labelLarge",
    sx = {},
    ...otherProps
}: OreveBlockButtonProps) => {
    const buttonSx = variantsSx[variant](selected)

    return (
        <StyledButton
            {...otherProps}
            disabled={loading || otherProps.disabled}
            variant={variant}
            color={color}
            sx={{
                ...buttonSx,
                ...sx,
            }}
        >
            <Typography variant={typography} color="inherit">
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "1em",
                        margin: computeMargin(value, removeMargin),
                    }}
                >
                    {icon && (
                        <Icon
                            sx={{
                                fontSize: "1em",
                                transform: "scale(1.5)",
                            }}
                            component={icon}
                        />
                    )}
                    {value !== "" && value}
                </Box>
            </Typography>
            {loading && (
                <CircularProgress
                    size={24}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                        color: `${color}.main`,
                    }}
                />
            )}
        </StyledButton>
    )
}
